var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { Matrix4, Points, Vector3 } from "three";
import { INTERSECTED, NOT_INTERSECTED } from "three-mesh-bvh";
import { assert, readBatchTable, readFeatureTable } from "../../../utils";
import { readBatchTableObject, readFeatureTableObject } from "./readTables";
import { readPointCloud } from "./readPointData";
import { PointCloudStyle } from "../../../../api";
import { ObjectType } from "../../../picking";
import { OptionsManager } from "../../../options";
export class PointCloud extends Points {
    constructor(buffer, material, featureTable, tile, materialRegistry, pickingManager, objectId, batchTable) {
        super(buffer, material);
        this.tileCost = OptionsManager.POINT_CLOUD_TILE_WEIGHT;
        this.tile = tile;
        this.materialRegistry = materialRegistry;
        this.pickingManager = pickingManager;
        this.objectId = objectId;
        this._featureTable = featureTable;
        this._batchTable = batchTable;
        this.styleCallbackFn = (v) => this.styleCallback(v);
        this.clippingPlaneCallbackFn = (v) => this.clippingPlaneCallback(v);
        this.invalidateCallbackFn = () => this.invalidateCallback();
        materialRegistry.styleCallbacks.add(this.styleCallbackFn);
        materialRegistry.clippingPlaneCallbacks.add(this.clippingPlaneCallbackFn);
        materialRegistry.invalidateCallbacks.add(this.invalidateCallbackFn);
    }
    static create(tile, data, styleDesc, materialRegistry, pickingManager, owner) {
        return __awaiter(this, void 0, void 0, function* () {
            // View of array buffer for reading
            const view = data;
            // Generate a new ID for this object
            const objectId = pickingManager.newId();
            // Check if the file header is valid ("pnts"). If not then we aren't reading a pnts file and should throw
            assert(PointCloud.isValidHeader(view));
            // We only support version 1, check the field in the header
            const version = view.getUint32(4, true);
            assert(version === 1);
            // Read the feature and batch table strings and data
            const featureTableData = readFeatureTable(view);
            const batchTableData = readBatchTable(view);
            // Parse the feature and batch table
            const featureTable = readFeatureTableObject(featureTableData);
            const batchTable = readBatchTableObject(batchTableData);
            // The geometry will be loaded differently depending on what extensions the point cloud uses.
            const [geometry, material] = yield readPointCloud(featureTableData, featureTable, batchTableData, batchTable, styleDesc, materialRegistry, objectId);
            const object = new PointCloud(geometry, material, featureTable, tile, materialRegistry, pickingManager, objectId, batchTable);
            PointCloud.resolveTransforms(object, featureTable, tile);
            object.updateMatrix();
            object.updateWorldMatrix(false, false);
            // Convert min and max gradient points to tile-relative space
            object.updateGradientVectors(styleDesc);
            // Register the object into the picking manager
            pickingManager.setForId(object.objectId, {
                objectType: ObjectType.Composite,
                rootObject: owner,
                subObject: object,
            });
            pickingManager.setIdForObject(object.id, object.objectId);
            return object;
        });
    }
    /**
     * Coerce an undefined PointCloudStyle to the default value
     * @param style
     */
    static styleOrDefault(style) {
        return style !== null && style !== void 0 ? style : PointCloudStyle.None;
    }
    static isValidHeader(view) {
        if (view.getUint8(0) !== 112)
            return false; // p
        if (view.getUint8(1) !== 110)
            return false; // n
        if (view.getUint8(2) !== 116)
            return false; // t
        if (view.getUint8(3) !== 115)
            return false; // s
        return true;
    }
    static resolveTransforms(object, featureTable, tile) {
        const matrix = new Matrix4();
        // RTC_CENTER is applied first, so translate by the value it holds
        if (typeof featureTable.rtcCenter !== "undefined") {
            matrix.makeTranslation(featureTable.rtcCenter[0], featureTable.rtcCenter[1], featureTable.rtcCenter[2]);
        }
        // Lastly we recursively apply the parent transforms until we reach the root. localTransform holds the result
        // of doing this to the parent tile so we only need to do a single matrix multiply here
        const localTransform = tile.extras.transform;
        const final = localTransform.clone().multiply(matrix);
        // Decompose the matrix into the object's position, rotation and scale as these are the authoritative source of
        // the object's transform
        final.decompose(object.position, object.quaternion, object.scale);
    }
    evict() {
        this.materialRegistry.styleCallbacks.delete(this.styleCallbackFn);
        this.materialRegistry.clippingPlaneCallbacks.delete(this.clippingPlaneCallbackFn);
        this.materialRegistry.invalidateCallbacks.delete(this.invalidateCallbackFn);
        if (this.objectId !== 0 && this.pickingManager !== undefined) {
            this.pickingManager.removeForId(this.objectId);
            this.pickingManager.removeForObject(this.id);
        }
        // Dispose of the geometry and drop the array buffers for the geometry
        this.geometry.dispose();
        // Dispose of the material
        this.material.dispose();
    }
    styleCallback(style) {
        var _a, _b, _c, _d, _e, _f, _g;
        this.updateGradientVectors(style);
        // If the style has changed then we need new shaders
        this.material.style = style.pointStyle || PointCloudStyle.None;
        this.material.classificationColors = (_a = style.classificationColors) !== null && _a !== void 0 ? _a : {};
        this.material.intensityColors = (_b = style.intensityColors) !== null && _b !== void 0 ? _b : [
            { r: 0, g: 0, b: 0 },
            { r: 1, g: 1, b: 1 },
        ];
        this.material.gradientColors = (_c = style.gradientColors) !== null && _c !== void 0 ? _c : [
            { r: 1, g: 0, b: 0 },
            { r: 0, g: 1, b: 0 },
        ];
        this.material.size = (_d = style.pointSize) !== null && _d !== void 0 ? _d : 1;
        this.material.brightness = style.brightness || 0;
        this.material.contrast = (_e = style.contrast) !== null && _e !== void 0 ? _e : 1;
        this.material.gamma = (_f = style.gamma) !== null && _f !== void 0 ? _f : 1;
        this.material.opacity = (_g = style.opacity) !== null && _g !== void 0 ? _g : 1;
        if (this.material.opacity < 1) {
            this.material.transparent = true;
            this.material.depthWrite = false;
        }
        else {
            this.material.transparent = false;
            this.material.depthWrite = true;
        }
        this.material.needsUpdate = true;
    }
    clippingPlaneCallback(planes) {
        this.material.clippingPlanes = planes;
        this.material.needsUpdate = true;
    }
    invalidateCallback() {
        this.material.needsUpdate = true;
    }
    // Internal function for converting gradient vectors to values sane to use
    updateGradientVectors(style) {
        var _a, _b, _c, _d, _e, _f;
        // TODO: This will not handle the case where the tileset moves *after* these values are calculated. We need to
        //       calculate the gradient positions relative to eye on the CPU and upload new values every frame to support
        //       moving with gradient colours. Three.js lacks a good way of implementing the required behavior, so I leave
        //       the issue unresolved for now.
        const minGradient = new Vector3(((_a = style.gradient) === null || _a === void 0 ? void 0 : _a.min.x) || 0, ((_b = style.gradient) === null || _b === void 0 ? void 0 : _b.min.y) || 0, ((_c = style.gradient) === null || _c === void 0 ? void 0 : _c.min.z) || 0);
        const maxGradient = new Vector3(((_d = style.gradient) === null || _d === void 0 ? void 0 : _d.max.x) || 0, ((_e = style.gradient) === null || _e === void 0 ? void 0 : _e.max.y) || 0, ((_f = style.gradient) === null || _f === void 0 ? void 0 : _f.max.z) || 0);
        const owner = this.tile.extras.owner.extras.owner;
        // TODO: this still won't handle when the tileset has been moved. if this tile is off screen when this is called
        //       then it won't know the full transform. No easy fix though, as I need to tileset's matrix
        const worldToLocal = owner.matrixWorld.clone().multiply(this.matrix).invert();
        this.material.minGradient = minGradient.applyMatrix4(worldToLocal);
        this.material.maxGradient = maxGradient.applyMatrix4(worldToLocal);
    }
    raycast(raycaster, intersects) {
        var _a, _b, _c;
        const ray = raycaster.ray.clone();
        const inverseMatrix = this.matrixWorld.clone().invert();
        ray.applyMatrix4(inverseMatrix);
        const threshold = (_b = (_a = raycaster.params.Points) === null || _a === void 0 ? void 0 : _a.threshold) !== null && _b !== void 0 ? _b : 0.01;
        const localThreshold = threshold / ((this.scale.x + this.scale.y + this.scale.z) / 3);
        const localThresholdSq = localThreshold * localThreshold;
        let closestDistanceSq = Infinity;
        let closestPoint = null;
        (_c = this.geometry.boundsTree) === null || _c === void 0 ? void 0 : _c.shapecast({
            boundsTraverseOrder: (box) => box.distanceToPoint(ray.origin),
            intersectsBounds: (box, isLeaf, score) => {
                if (!score || score * score > closestDistanceSq) {
                    return NOT_INTERSECTED;
                }
                box.expandByScalar(localThreshold);
                return ray.intersectsBox(box) ? INTERSECTED : NOT_INTERSECTED;
            },
            intersectsTriangle: (triangle) => {
                // Track the closest found point distance
                const distanceToRaySq = ray.distanceSqToPoint(triangle.a);
                if (distanceToRaySq < localThresholdSq) {
                    const distanceToPointSq = ray.origin.distanceToSquared(triangle.a);
                    if (distanceToPointSq < closestDistanceSq) {
                        closestDistanceSq = distanceToPointSq;
                        closestPoint = triangle.a.clone().applyMatrix4(this.matrixWorld);
                    }
                }
            },
        });
        if (closestDistanceSq < Infinity && closestPoint) {
            intersects.push({
                distance: Math.sqrt(closestDistanceSq),
                point: closestPoint,
                object: this,
            });
        }
    }
}
